import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { DefaultConstant, syncCafeStatus } from "../../common/commonConstants";
import * as diningReducer from "../../reducers/diningReducer";
import * as diningSelector from "../../selectors/diningSelector";
import { useAppDispatch } from "../../store/store";
import withAppInsights from "../AppInsights/AppInsights";
import CafeSelectorView from "./CafeSelectorView";

type DropdownOption = { key: string; text: string };

// Option selector construction, in order of appearance
const regionOptionsSelector = createSelector(diningSelector.regionListSelector, (regionList) =>
  regionList.map((region) => ({ key: region.id.toString(), text: region.name ?? "" })),
);

const cafeOptionsSelector = createSelector(diningSelector.syncCafeListSelector, (syncCafeList) =>
  syncCafeList.map((cafe) => ({ key: cafe.id.toString(), text: cafe.name ?? "" })),
);

export const CafeSyncSelector = () => {
  const regionOptions = useSelector(regionOptionsSelector);
  const cafeOptions = useSelector(cafeOptionsSelector);
  const selectedSyncCafe = useSelector(diningSelector.syncCafeSelector);
  const selectedSyncRegion = useSelector(diningSelector.syncRegionSelector);
  const syncCafeSelectorList = useSelector(diningSelector.syncCafeListSelector);

  const dispatch = useAppDispatch();

  // Fetches region list on initialization
  useEffect(() => {
    if (!regionOptions || regionOptions.length == 0) dispatch(diningReducer.fetchRegionsAsync());
  }, []);

  const onRegionSelect = (option?: DropdownOption) => {
    if (option) {
      const region = regionOptions.find((obj) => obj.key === option.key);
      if (region) {
        dispatch(diningReducer.setSelectedSyncRegionAsync(region.key));
      }
    }
    option && dispatch(diningReducer.fetchRegionSyncCafesAsync(option.key, DefaultConstant.AGILYSYS));
  };

  // Fires onChange of Cafe Selection Dropdown
  const onCafeSyncSelect = (option?: DropdownOption) => {
    if (option) {
      dispatch(diningReducer.resetSyncCafeAndFlushCacheStatus(syncCafeStatus.IDLE));

      const cafe = syncCafeSelectorList.find((obj) => obj.id === option.key);
      if (cafe) {
        dispatch(diningReducer.fetchSelectedSyncCafeAsync(option.key));
      }
    }
  };

  return (
    <CafeSelectorView
      regionOptions={regionOptions}
      cafeOptions={cafeOptions}
      onRegionSelect={onRegionSelect}
      onCafeSelect={onCafeSyncSelect}
      selectedRegionId={selectedSyncRegion ? selectedSyncRegion : ""}
      selectedCafeId={selectedSyncCafe?.id}
    />
  );
};

export default withAppInsights(CafeSyncSelector);
